import { useState, type ReactElement } from 'react';
import ReactEcharts from 'echarts-for-react';
import * as XLSX from 'xlsx';
import { useTheme } from '../../../../../theme-context';
import {
    useGetClaireBookingFailureReasonsQuery,
    useGetTransferredReasonsQuery,
} from '../../../../../store/excecutive/executiveApi';
import { LOGIN_HERO_IMAGE } from '../../../../../config/env';
import { readFromStorage } from '../../../../../utils/webStorage';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const MonthlyStats = require('./MonthlyStats').default;

const CALL_TRACKING = [
    {
        label: 'Total Calls Allocated',
        valueType: 'number',
        definition: 'Total calls allocated to Claire in period.',
    },
    {
        label: 'Containment Rate',
        valueType: 'percent',
        definition: 'Caller’s needs were met.',
    },
    {
        label: 'Booking Rate',
        valueType: 'percent',
        definition:
            'Percentage of calls to Claire that ended in a successful booking.',
    },
    {
        label: 'Transfer Rate',
        valueType: 'percent',
        definition: 'Caller was transferred to Agent.',
    },
    {
        label: 'Missed Calls Rate',
        valueType: 'percent',
        definition: 'Calls disconnected by caller before Claire answered.',
    },
    {
        label: 'Total Reservations Confirmed',
        valueType: 'number',
        definition: 'Number of Bookings in period.',
    },
    {
        label: 'Booking Value',
        valueType: 'currency',
        definition: 'Value of bookings in period.',
    },
    {
        label: 'Booking Failures',
        valueType: 'number',
        definition:
            'Reservation flow initiated but call terminated by customer.',
    },
    {
        label: 'Average Call Duration',
        valueType: 'avg_call_duration_sec',
        definition:
            'Mean call length in the selected period. Min and max are the shortest and longest call durations (seconds).',
    },
];

const formatCallDurationSec = (sec: unknown): string => {
    const n = Number(sec);
    if (!Number.isFinite(n) || n < 0) {
        return '—';
    }
    if (n < 60) {
        return n % 1 === 0 ? `${Math.round(n)}s` : `${n.toFixed(1)}s`;
    }
    const mins = Math.floor(n / 60);
    const rem = n - mins * 60;
    const remRounded = Math.round(rem * 10) / 10;
    const remStr =
        remRounded % 1 === 0
            ? `${Math.round(remRounded)}`
            : remRounded.toFixed(1);
    return `${mins}m ${remStr}s`;
};

const getByPath = (obj: any, path: string, fallback = 0) => {
    if (!obj || !path) return fallback;

    return (
        path
            .split('.')
            .reduce(
                (acc, key) =>
                    acc && acc[key] !== undefined ? acc[key] : undefined,
                obj,
            ) ?? fallback
    );
};

const formatReasonLabel = (value?: string) => {
    if (!value) return 'Unknown';
    return value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatExportDateTime = (value: unknown) => {
    if (!value) return '';
    const date = new Date(String(value));
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const ClairePromptTracking = ({
    rightNode,
    title,
    kpis,
    kpiView,
    onViewTransferDetails,
    onViewBookingFailureDetails,
}: {
    rightNode: ReactElement;
    title?: string;
    kpis?: any;
    kpiView: 'today' | '7day' | '30day';
    onViewTransferDetails?: () => void;
    onViewBookingFailureDetails?: () => void;
}) => {
    const { theme } = useTheme();
    const [showTransferRateModal, setShowTransferRateModal] = useState(false);
    const [showBookingFailureModal, setShowBookingFailureModal] = useState(false);
    const HOTEL_SUB = '921544e4-20c1-703b-c1d4-62f60c91cf49';
    const {
        data: transferredReasonsData,
        isLoading: transferredReasonsLoading,
        isError: transferredReasonsError,
    } = useGetTransferredReasonsQuery(
        {
            hotelSub: HOTEL_SUB,
            view: kpiView,
        },
        {
            skip: !showTransferRateModal,
        },
    );
    const {
        data: bookingFailureData,
        isLoading: bookingFailureLoading,
        isError: bookingFailureError,
    } = useGetClaireBookingFailureReasonsQuery(
        {
            hotelSub: HOTEL_SUB,
            view: kpiView,
        },
        {
            skip: !showBookingFailureModal,
        },
    );

    const getCardValue = (label: string, valueType: string): string | number => {
        if (label === 'Average Call Duration') {
            return formatCallDurationSec(
                getByPath(kpis, 'averageCallDurationSec', NaN),
            );
        }

        const keyMap: Record<string, string> = {
            'Total Calls Allocated': 'totalCallsAllocated',
            'Booking Rate': 'bookingRate',
            'Containment Rate': 'containmentRate',
            'Transfer Rate': 'transferRate',
            'Missed Calls Rate': 'abandonmentRate',
            'Total Reservations Confirmed': 'totalReservationsConfirmed',
            'Booking Value': 'totalBookingValue',
            'Booking Failures': 'bookingFailureCount',
        };

        const rawValue = Number(getByPath(kpis, keyMap[label], 0));

        if (valueType === 'percent') {
            return `${rawValue.toFixed(2)}%`;
        }

        if (valueType === 'currency') {
            return `$${rawValue.toLocaleString()}`;
        }

        return Number.isFinite(rawValue) ? rawValue : 0;
    };

    const donutOption = {
        tooltip: {
            trigger: 'item',
            formatter: (params: any) => {
                const percentage =
                    params?.data?.percentage !== undefined
                        ? Number(params.data.percentage).toFixed(2)
                        : params?.percent?.toFixed?.(2) || '0.00';
                return `${params.name}: ${params.value} (${percentage}%)`;
            },
        },
        color: ['#94A3B8', '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#A855F7'],
        graphic: [
            {
                type: 'text',
                left: '68%',
                top: '46%',
                style: {
                    text: `${bookingFailureData?.totalFailures || 0}`,
                    fill: theme === 'dark' ? '#ffffff' : '#111827',
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: 'center',
                },
            },
            {
                type: 'text',
                left: '68%',
                top: '57%',
                style: {
                    text: 'TOPICS',
                    fill: '#6B7280',
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: 'center',
                },
            },
        ],
        series: [
            {
                type: 'pie',
                radius: ['50%', '72%'],
                center: ['68%', '50%'],
                avoidLabelOverlap: true,
                label: { show: false },
                labelLine: { show: false },
                data:
                    bookingFailureData?.reasons?.map((item) => ({
                        name: formatReasonLabel(item.reason),
                        value: item.count,
                        percentage: item.percentage,
                    })) || [],
            },
        ],
    };

    const transferDonutOption = {
        tooltip: {
            trigger: 'item',
            formatter: (params: any) => {
                const percentage =
                    params?.data?.percentage !== undefined
                        ? Number(params.data.percentage).toFixed(2)
                        : params?.percent?.toFixed?.(2) || '0.00';
                return `${params.name}: ${params.value} (${percentage}%)`;
            },
        },
        color: ['#94A3B8', '#3B82F6', '#EF4444', '#F59E0B', '#10B981', '#A855F7'],
        graphic: [
            {
                type: 'text',
                left: '68%',
                top: '46%',
                style: {
                    text: `${transferredReasonsData?.totalTransferred || 0}`,
                    fill: theme === 'dark' ? '#ffffff' : '#111827',
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: 'center',
                },
            },
            {
                type: 'text',
                left: '68%',
                top: '57%',
                style: {
                    text: 'TOPICS',
                    fill: '#6B7280',
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: 'center',
                },
            },
        ],
        series: [
            {
                type: 'pie',
                radius: ['50%', '72%'],
                center: ['68%', '50%'],
                avoidLabelOverlap: true,
                label: { show: false },
                labelLine: { show: false },
                data:
                    transferredReasonsData?.reasons?.map((item) => ({
                        name: formatReasonLabel(item.reason),
                        value: item.count,
                        percentage: item.percentage,
                    })) || [],
            },
        ],
    };

    const downloadAnalyticsXlsx = async ({
        endpoint,
        fallbackSheetName,
        fallbackFileName,
        logContext,
    }: {
        endpoint: string;
        fallbackSheetName: string;
        fallbackFileName: string;
        logContext: string;
    }) => {
        try {
            const payload = {
                data: {
                    hotelSub: HOTEL_SUB,
                    view: kpiView,
                },
            };
            const query = new URLSearchParams({
                hotelSub: HOTEL_SUB,
                view: kpiView,
            });
            const deviceInfoApiBase = (
                process.env.REACT_APP_DEVICE_INFO_API_URL || ''
            ).replace(/\/$/, '');
            const endpointPath = `${endpoint}?${query.toString()}`;

            const response = await fetch(
                `${deviceInfoApiBase}${endpointPath}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(readFromStorage('accessToken')
                            ? {
                                  Authorization: `Bearer ${readFromStorage(
                                      'accessToken',
                                  )}`,
                              }
                            : {}),
                    },
                    body: JSON.stringify(payload),
                },
            );

            if (!response.ok) {
                throw new Error('Failed to fetch booking failure export data');
            }

            const exportData = await response.json();
            const columns: Array<{
                header: string;
                key: string;
                width?: number;
            }> = exportData?.columns || [];
            const rows: Array<Record<string, any>> = exportData?.rows || [];

            // Move period metadata rows to the end of the sheet.
            const isPeriodRow = (row: Record<string, any>) => {
                const reason = String(row?.reason || '').trim().toLowerCase();
                return reason === 'period start' || reason === 'period end';
            };
            const nonPeriodRows = rows.filter((row) => !isPeriodRow(row));
            const periodRows = rows.filter((row) => isPeriodRow(row));
            const orderedRows = [...nonPeriodRows, ...periodRows];

            if (!columns.length) {
                throw new Error('Export columns are missing');
            }

            const headerRow = columns.map((column) => column.header);
            const dataRows = orderedRows.map((row) =>
                columns.map((column) => {
                    const key = String(column.key || '').trim();
                    const value = row?.[key];

                    if (key === 'reason') {
                        return formatReasonLabel(value);
                    }

                    // Period Start/End rows often store the timestamp in the "Count" column (or similar).
                    // Format any parsable date-like value for those rows regardless of the column key.
                    if (isPeriodRow(row) && value) {
                        const asString = String(value);
                        if (asString.includes('T') && (asString.endsWith('Z') || asString.includes('+'))) {
                            return formatExportDateTime(asString);
                        }
                    }

                    return value ?? '';
                }),
            );

            const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
            worksheet['!cols'] = columns.map((column) => ({
                wch: column.width || 20,
            }));

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                exportData?.sheetName || fallbackSheetName,
            );

            XLSX.writeFile(
                workbook,
                exportData?.fileName ||
                    `${fallbackFileName}-${kpiView}-${Date.now()}.xlsx`,
            );
        } catch (error) {
            console.error(`Failed to download ${logContext} report:`, error);
            alert('Failed to download .xlsx file. Please try again.');
        }
    };

    const onDownloadBookingFailureXlsx = async () => {
        await downloadAnalyticsXlsx({
            endpoint: '/spa-voice-agent/analytics/booking-failure-reasons/download',
            fallbackSheetName: 'Booking Failure Reasons',
            fallbackFileName: 'booking-failure-reasons',
            logContext: 'booking failure',
        });
    };

    const onDownloadTransferredReasonsXlsx = async () => {
        await downloadAnalyticsXlsx({
            endpoint: '/spa-voice-agent/analytics/transferred-reasons/download',
            fallbackSheetName: 'Transferred Reasons',
            fallbackFileName: 'transferred-reasons',
            logContext: 'transferred reasons',
        });
    };

    return (
        <div>
            {showTransferRateModal && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="w-full max-w-5xl rounded-2xl bg-white dark:bg-[#111827] p-6 border border-gray-200 dark:border-gray-700 max-h-[92vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-2xl font-semibold dark:text-white">
                                Transfer Rate
                            </h3>
                            <button
                                className="text-xl text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
                                onClick={() => setShowTransferRateModal(false)}>
                                x
                            </button>
                        </div>
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4">
                            {transferredReasonsLoading ? (
                                <p className="text-sm text-gray-500 dark:text-gray-300">
                                    Loading transfer reasons...
                                </p>
                            ) : transferredReasonsError ? (
                                <p className="text-sm text-red-600 dark:text-red-400">
                                    Failed to load transfer reasons.
                                </p>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                                    <div className="rounded-lg border border-gray-100 dark:border-gray-800 p-4">
                                        <p className="text-base font-semibold text-gray-700 dark:text-gray-200">
                                            Transfer Rate Reasons
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                                            Total topics: {transferredReasonsData?.totalTransferred || 0}
                                        </p>
                                        <div className="space-y-3">
                                            {transferredReasonsData?.reasons?.map((item, index) => (
                                                <div key={`${item.reason}-${index}`} className="flex items-start">
                                                    <span
                                                        className="w-2 h-2 rounded-full mt-1.5 mr-2"
                                                        style={{
                                                            backgroundColor: ['#94A3B8', '#3B82F6', '#EF4444', '#F59E0B', '#10B981', '#A855F7'][index % 6],
                                                        }}
                                                    />
                                                    <div>
                                                        <p className="text-sm text-gray-700 dark:text-gray-200">
                                                            {formatReasonLabel(item.reason)}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {item.count} ({Number(item.percentage || 0).toFixed(2)}%)
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <ReactEcharts
                                        option={transferDonutOption}
                                        style={{ height: 280, width: '100%' }}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-4">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-[#1F2937]">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-medium dark:text-gray-200">
                                            Reason
                                        </th>
                                        <th className="text-left px-4 py-3 font-medium dark:text-gray-200">
                                            Number
                                        </th>
                                        <th className="text-left px-4 py-3 font-medium dark:text-gray-200">
                                            Percentage
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transferredReasonsData?.reasons?.map((item, index) => (
                                        <tr
                                            key={`${item.reason}-${index}`}
                                            className="border-t border-gray-100 dark:border-gray-800">
                                            <td className="px-4 py-3 dark:text-gray-200">
                                                {formatReasonLabel(item.reason)}
                                            </td>
                                            <td className="px-4 py-3 dark:text-gray-200">
                                                {item.count}
                                            </td>
                                            <td className="px-4 py-3 dark:text-gray-200">
                                                {Number(item.percentage || 0).toFixed(2)}%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex justify-between gap-3">
                            <button
                                onClick={() => {
                                    setShowTransferRateModal(false);
                                    onViewTransferDetails?.();
                                }}
                                className="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200 rounded-lg px-4 py-2 text-sm min-w-44">
                                View Call Details
                            </button>
                            <button
                                onClick={onDownloadTransferredReasonsXlsx}
                                className="bg-[#00330F] text-white rounded-lg px-4 py-2 text-sm min-w-44">
                                Download .xlsx
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showBookingFailureModal && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="w-full max-w-5xl rounded-2xl bg-white dark:bg-[#111827] p-6 border border-gray-200 dark:border-gray-700 max-h-[92vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-2xl font-semibold dark:text-white">
                                Booking Failure Count
                            </h3>
                            <button
                                className="text-xl text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
                                onClick={() => setShowBookingFailureModal(false)}>
                                x
                            </button>
                        </div>
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4">
                            {bookingFailureLoading ? (
                                <p className="text-sm text-gray-500 dark:text-gray-300">
                                    Loading booking failure reasons...
                                </p>
                            ) : bookingFailureError ? (
                                <p className="text-sm text-red-600 dark:text-red-400">
                                    Failed to load booking failure reasons.
                                </p>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                                    <div className="rounded-lg border border-gray-100 dark:border-gray-800 p-4">
                                        <p className="text-base font-semibold text-gray-700 dark:text-gray-200">
                                            Booking Failure Count Reasons
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                                            Total topics: {bookingFailureData?.totalFailures || 0}
                                        </p>
                                        <div className="space-y-3">
                                            {bookingFailureData?.reasons?.map((item, index) => (
                                                <div key={`${item.reason}-${index}`} className="flex items-start">
                                                    <span
                                                        className="w-2 h-2 rounded-full mt-1.5 mr-2"
                                                        style={{
                                                            backgroundColor: ['#94A3B8', '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#A855F7'][index % 6],
                                                        }}
                                                    />
                                                    <div>
                                                        <p className="text-sm text-gray-700 dark:text-gray-200">
                                                            {formatReasonLabel(item.reason)}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {item.count} ({Number(item.percentage || 0).toFixed(2)}%)
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <ReactEcharts
                                        option={donutOption}
                                        style={{ height: 280, width: '100%' }}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-4">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-[#1F2937]">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-medium dark:text-gray-200">
                                            Reason
                                        </th>
                                        <th className="text-left px-4 py-3 font-medium dark:text-gray-200">
                                            Number
                                        </th>
                                        <th className="text-left px-4 py-3 font-medium dark:text-gray-200">
                                            Percentage
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bookingFailureData?.reasons?.map((item, index) => (
                                        <tr
                                            key={`${item.reason}-${index}`}
                                            className="border-t border-gray-100 dark:border-gray-800">
                                            <td className="px-4 py-3 dark:text-gray-200">
                                                {formatReasonLabel(item.reason)}
                                            </td>
                                            <td className="px-4 py-3 dark:text-gray-200">
                                                {item.count}
                                            </td>
                                            <td className="px-4 py-3 dark:text-gray-200">
                                                {Number(item.percentage || 0).toFixed(2)}%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex justify-between gap-3">
                            <button
                                onClick={() => {
                                    setShowBookingFailureModal(false);
                                    onViewBookingFailureDetails?.();
                                }}
                                className="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200 rounded-lg px-4 py-2 text-sm min-w-44">
                                View Call Details
                            </button>
                            <button
                                onClick={onDownloadBookingFailureXlsx}
                                className="bg-[#00330F] text-white rounded-lg px-4 py-2 text-sm min-w-44">
                                Download .xlsx
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="mt-2 mb-3 flex flex-col md:flex-row flex-wrap md:items-center">
                <h4
                    className="mb-1 text-[20px] leading-[20px] font-semibold tracking-[0px] text-[#101010] dark:text-white"
                    style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
                    {title || 'Call Tracking'}
                </h4>
                <div className="md:ml-auto flex flex-col md:flex-row no-print">
                    {rightNode}
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[12px] mb-4">
                {CALL_TRACKING.map((call: any) => (
                    <div
                        key={call.label}
                        className="w-full">
                        <div
                            key={call.label}
                            className="relative h-[104px] w-full rounded-[14px] border border-[#00330F] px-5 py-4 flex flex-col justify-between gap-[6px] overflow-visible dark:bg-[#1F1F1F]"
                            onClick={() => {
                                if (call.label === 'Transfer Rate') {
                                    setShowTransferRateModal(true);
                                }
                                if (call.label === 'Booking Failures') {
                                    setShowBookingFailureModal(true);
                                }
                            }}>
                            <div className="flex items-center gap-1">
                                <p
                                    className="text-sm font-semibold leading-[1.2] tracking-[0px] text-[#101010] dark:text-white"
                                    style={{
                                        fontFamily: 'Inter, Arial, sans-serif',
                                    }}>
                                    {call.label}
                                </p>
                                <div className="relative group">
                                    <button
                                        type="button"
                                        aria-label={`${call.label} definition`}
                                        className="ml-1 mb-1 h-4 w-4 flex items-center justify-center">
                                        <img
                                            src="/svg/info-3.png"
                                            alt=""
                                            className="h-4 w-4 object-contain"
                                        />
                                    </button>
                                    <div className="pointer-events-none absolute left-0 top-6 z-20 w-56 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0f172a] p-2 text-xs text-gray-700 dark:text-gray-200 shadow-lg opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
                                        {call.definition}
                                    </div>
                                </div>
                            </div>
                            {call.label === 'Average Call Duration' ? (
                                <div className="flex flex-col gap-0.5">
                                    <p
                                        className="mb-0 text-[1.5rem] leading-none font-medium tracking-[0px] text-[#00330F] dark:text-white"
                                        style={{
                                            fontFamily:
                                                '"Plus Jakarta Sans", Inter, Arial, sans-serif',
                                        }}>
                                        {getCardValue(
                                            call.label,
                                            call.valueType,
                                        )}
                                    </p>
                                    <div
                                        className="flex flex-wrap items-baseline gap-x-2 gap-y-0 pt-1 text-[10px] leading-tight text-[#5C5C5C] dark:text-gray-400"
                                        style={{
                                            fontFamily:
                                                'Inter, Arial, sans-serif',
                                        }}>
                                        <span>
                                            <span className="font-semibold text-[#101010] dark:text-gray-300">
                                                Min
                                            </span>{' '}
                                            {formatCallDurationSec(
                                                getByPath(
                                                    kpis,
                                                    'minCallDurationSec',
                                                    NaN,
                                                ),
                                            )}
                                        </span>
                                        <span>
                                            <span className="font-semibold text-[#101010] dark:text-gray-300">
                                                Max
                                            </span>{' '}
                                            {formatCallDurationSec(
                                                getByPath(
                                                    kpis,
                                                    'maxCallDurationSec',
                                                    NaN,
                                                ),
                                            )}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <p
                                    className="mb-1 text-[1.5rem] leading-[1.2] font-medium tracking-[0px] text-[#00330F] dark:text-white"
                                    style={{
                                        fontFamily:
                                            '"Plus Jakarta Sans", Inter, Arial, sans-serif',
                                    }}>
                                    {getCardValue(call.label, call.valueType)}
                                </p>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {LOGIN_HERO_IMAGE === '/images/claire-glen-ivy-login.jpeg' ? (
                <MonthlyStats />
            ) : null}
        </div>
    );
};

export default ClairePromptTracking;
